import { QueryClient } from "@tanstack/react-query";
import { readOverviewSnapshot } from "../api/hooks";
import { CompositeDevice, DeviceMapData } from "../types/overview";

type DeviceInfos = {
    deviceId: string | undefined;
    deviceType: string;
    displayName: string | undefined;
    deviceFeatures: string[] | undefined;
    defaultFeatureRef: string | undefined;
    isComposite: boolean;
};

type FoundDevice = {
    device: DeviceMapData | CompositeDevice;
    isComposite: boolean;
};

function findDevice(
    devices: DeviceMapData[],
    deviceId: string
): FoundDevice | undefined {
    const device = devices.find((device) => device.id === deviceId);

    if (device) {
        return {
            device,
            isComposite: device.isComposite ?? false,
        };
    }

    for (const device of devices) {
        const compositeDevice = device.compositeDevices?.find(
            (compositeDevice) => compositeDevice.id === deviceId
        );

        if (compositeDevice) {
            return {
                device: compositeDevice,
                isComposite: false,
            };
        }
    }

    return undefined;
}

export function getDeviceInfos(
    queryClient: QueryClient,
    id: string | undefined
): DeviceInfos {
    if (!id) {
        return {
            deviceId: undefined,
            deviceType: "",
            displayName: undefined,
            deviceFeatures: undefined,
            defaultFeatureRef: undefined,
            isComposite: false,
        };
    }

    const overview = readOverviewSnapshot(true);

    if (!overview) {
        return {
            deviceId: undefined,
            deviceType: "",
            displayName: undefined,
            deviceFeatures: undefined,
            defaultFeatureRef: undefined,
            isComposite: false,
        };
    }

    const result = findDevice(overview.deviceMapData, id);

    if (!result) {
        return {
            deviceId: undefined,
            deviceType: "",
            displayName: undefined,
            deviceFeatures: undefined,
            defaultFeatureRef: undefined,
            isComposite: false,
        };
    }

    const { device, isComposite } = result;

    return {
        deviceId: device.datasourceId,
        deviceType: device.type !== ""
            ? device.type
            : device.typeDesign !== ""
                ? device.typeDesign
                : "",
        displayName: device.name,
        deviceFeatures: "deviceFeatures" in device
            ? device.deviceFeatures
            : [],
        defaultFeatureRef:
            "defaultFeatureRef" in device
                ? device.defaultFeatureRef ?? undefined
                : undefined,
        isComposite,
    };
}
